import React from "react";
import styled from "styled-components";
import {
  ArrowsClockwise,
  Lightbulb,
  ShareNetwork,
} from "@phosphor-icons/react";

import { useEyedropper } from "../../contexts/EyedropperContext";
import { usePalette } from "../../contexts/PaletteContext";
import useIsMobile from "../../hooks/useIsMobile";
import ActionButton from "../ActionButton";
import ColorEditor from "../ColorEditor";
import LightingEditor from "../LightingEditor";
import Modal, { ModalTitle } from "../Modal";
import Panel from "../Panel";
import ToolPopover from "../ToolPopover";
import ToolSlider from "../ToolSlider";

import { MOBILE_BREAKPOINT, TABLET_BREAKPOINT } from "../../constants";
function CreativePanel({ creative, onChangeMarc, onSyndicate }) {
  const {
    onionOpacity,
    setOnionOpacity,
    warmth,
    setWarmth,
    redshift,
    setRedshift,
    eggLights,
    setEggLights,
  } = creative;
  const { palette, activeColorIndex, setPaletteSlot } = usePalette();
  const { active: eyedropperActive } = useEyedropper();
  const isMobile = useIsMobile();

  const [openPopover, setOpenPopover] = React.useState(null);
  const closeIfRequested = (open) => {
    if (!open) setOpenPopover(null);
  };

  const activeHex = palette[activeColorIndex];
  const handleColorChange = (hex) => setPaletteSlot?.(activeColorIndex, hex);

  return (
    <StyledPanel>
      <Stack>
        <Columns>
          <LeftCol>
            <Header>Make Your Marc</Header>
            <Field>
              <RowLabel>Marc Opacity</RowLabel>
              <ToolSlider
                min={0}
                max={1}
                step={0.05}
                value={onionOpacity}
                onValueChange={setOnionOpacity}
                ariaLabel="Marc opacity"
              />
            </Field>
            <Field>
              <RowLabel>Warmth</RowLabel>
              <ToolSlider
                min={0}
                max={100}
                value={warmth}
                onValueChange={setWarmth}
                ariaLabel="Warmth"
              />
            </Field>
            <Field>
              <RowLabel>Redshift</RowLabel>
              <ToolSlider
                min={0}
                max={100}
                value={redshift}
                onValueChange={setRedshift}
                ariaLabel="Redshift"
              />
            </Field>
          </LeftCol>

          <RightCol>
            {isMobile ? (
              <>
                <ActionButton
                  type="button"
                  $active={openPopover === "lights"}
                  onClick={() =>
                    setOpenPopover((p) => (p === "lights" ? null : "lights"))
                  }
                  title="Edit the egg's lights"
                >
                  <Lightbulb size={14} weight="fill" />
                  <span>Edit Lights</span>
                </ActionButton>
                <Modal
                  open={openPopover === "lights"}
                  onOpenChange={closeIfRequested}
                  width={420}
                  bottomOnMobile
                >
                  <ModalTitle>Edit Lights</ModalTitle>
                  <LightingEditor
                    lights={Array.isArray(eggLights) ? eggLights : []}
                    setLights={setEggLights}
                  />
                </Modal>
              </>
            ) : (
              <ToolPopover
                open={openPopover === "lights"}
                onOpenChange={closeIfRequested}
                side="left"
                align="start"
                trigger={
                  <ActionButton
                    type="button"
                    $active={openPopover === "lights"}
                    onClick={() =>
                      setOpenPopover((p) => (p === "lights" ? null : "lights"))
                    }
                    title="Edit the egg's lights"
                  >
                    <Lightbulb size={14} weight="fill" />
                    <span>Edit Lights</span>
                  </ActionButton>
                }
              >
                <LightingEditor
                  lights={Array.isArray(eggLights) ? eggLights : []}
                  setLights={setEggLights}
                />
              </ToolPopover>
            )}

            <ToolPopover
              open={openPopover === "color"}
              onOpenChange={closeIfRequested}
              side={isMobile ? "top" : "left"}
              align={isMobile ? "end" : "start"}
              lockOpen={eyedropperActive}
              trigger={
                <ActionButton
                  type="button"
                  $active={openPopover === "color"}
                  onClick={() =>
                    setOpenPopover((p) => (p === "color" ? null : "color"))
                  }
                  title="Replace the active color"
                >
                  <Swatch $hex={activeHex} aria-hidden />
                  <span>Edit Color</span>
                </ActionButton>
              }
            >
              <ColorEditor
                value={activeHex}
                onChange={handleColorChange}
                onClose={() => setOpenPopover(null)}
              />
            </ToolPopover>

            <ActionButton
              type="button"
              onClick={() => {
                setOpenPopover(null);
                onChangeMarc();
              }}
              title="Pick a different Marc"
            >
              <ArrowsClockwise size={14} weight="bold" />
              <span>Pivot Marcs</span>
            </ActionButton>

            <ActionButton
              type="button"
              onClick={() => {
                setOpenPopover(null);
                onSyndicate?.();
              }}
              title="Submit your egg to the gallery and grab share images"
            >
              <ShareNetwork size={14} weight="fill" />
              <span>Syndicate Marc</span>
            </ActionButton>
          </RightCol>
        </Columns>
      </Stack>
    </StyledPanel>
  );
}

const StyledPanel = styled(Panel)`
  padding: 10px 14px 12px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    padding: 6px 12px 8px;
  }
`;

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  height: 100%;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 4px;
  }
`;

const Header = styled.h3`
  margin: 0;
  font-family: "Fraunces", serif;
  font-size: 1.25rem;
  font-weight: 600;
  padding-bottom: 0.5rem;
  color: var(--color-orange-700);
  line-height: 1;
  display: flex;
  align-items: center;
  gap: 10px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    font-size: 1.15rem;
    padding-bottom: 0.15rem;
  }

  // nroyalty: i think maybe we don't want this, not sure.
  /* &::after {
    content: "";
    flex: 1;
    height: 1px;
    transform: translateY(4px);
    background: var(--color-orange-300);
  } */
`;

const Columns = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  column-gap: 16px;
  flex: 1;
  min-height: 0;
`;

const LeftCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 6px;
`;

const RightCol = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 6px;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    gap: 0px;
  }
`;

const RowLabel = styled.span`
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: var(--color-text-muted);
  white-space: nowrap;
`;

const Swatch = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: ${(p) => p.$hex};
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.25);
`;

export default CreativePanel;
